import {useWebVitals} from '@github-ui/use-web-vitals'
import {sendCustomMetric} from '@github-ui/stats'

export type LandingPageWebVitalsTrackerProps = {
  /** Page identifier derived from pathname (e.g., 'features.spark', 'features.copilot.cli', 'enterprise') */
  page: string
}

/**
 * Tracks Web Vitals (HPC, CLS, LCP, INP) for landing pages.
 *
 * This component listens to Web Vitals events and sends custom metrics to Datadog
 * with page-level tags. The page identifier uses period-delimited format: /features/copilot/cli → features.copilot.cli
 *
 * This component tracks whichever `page` identifier it is given. At the app level,
 * callers typically render it only for /features/* pages by default, and may use
 * the `landing_pages_web_vitals_all_pages` feature flag (or similar) to opt other
 * landing pages into tracking. The identifier is limited to 4 path segments to
 * keep tag cardinality bounded.
 *
 * @example
 * ```tsx
 * <LandingPageWebVitalsTracker page="features.copilot.cli" />
 * ```
 */
export function LandingPageWebVitalsTracker({page}: LandingPageWebVitalsTrackerProps) {
  const tags: Record<string, string> = {page}

  useWebVitals('hpc', event => {
    if (!event.detail.hpc) return
    sendCustomMetric({
      name: 'LANDING_PAGES_VITALS_HPC',
      value: event.detail.hpc,
      tags,
    })
  })

  useWebVitals('cls', event => {
    if (!event.detail.cls) return
    sendCustomMetric({
      name: 'LANDING_PAGES_VITALS_CLS',
      value: event.detail.cls,
      tags,
    })
  })

  useWebVitals('lcp', event => {
    if (!event.detail.lcp) return
    sendCustomMetric({
      name: 'LANDING_PAGES_VITALS_LCP',
      value: event.detail.lcp,
      tags,
    })
  })

  useWebVitals('inp', event => {
    if (!event.detail.inp) return
    sendCustomMetric({
      name: 'LANDING_PAGES_VITALS_INP',
      value: event.detail.inp,
      tags,
    })
  })

  return null
}
